export type CircuitBreakerPolicy = {
  failureThreshold: number;
  failureWindowMs: number;
  halfOpenMaxProbes: number;
  openMs: number;
};

export type OutboundRequestPolicy = {
  circuitBreaker?: CircuitBreakerPolicy;
  hostConcurrency?: number;
  hosts: string[];
  name: string;
  pathPrefixes?: string[];
  queueTimeoutMs?: number;
  timeoutMs?: number;
};

type CatalogOutboundRequestPolicy = OutboundRequestPolicy & { developmentOnly?: boolean };

const OUTBOUND_REQUEST_POLICIES: CatalogOutboundRequestPolicy[] = [
  {
    developmentOnly: true,
    hostConcurrency: 24,
    hosts: [
      'cloudflared-api.benzinga.com',
      'content-server.prod.api.benzinga.dev',
      'content-server.staging.api.benzinga.dev',
    ],
    name: 'internal-news-dev',
    queueTimeoutMs: 4000,
    timeoutMs: 8000,
  },
  {
    developmentOnly: true,
    hostConcurrency: 24,
    hosts: ['api.benzinga.com'],
    name: 'public-news-dev',
    queueTimeoutMs: 4000,
    timeoutMs: 8000,
  },
  {
    circuitBreaker: {
      failureThreshold: 10,
      failureWindowMs: 30000,
      halfOpenMaxProbes: 1,
      openMs: 30000,
    },
    hostConcurrency: 16,
    hosts: ['chart-api-next.core-prod', 'chart-api-next.core-staging'],
    name: 'chart-api',
    queueTimeoutMs: 5000,
    timeoutMs: 5000,
  },
  {
    // Timeout stays under the scanner client's first 3s retry window so attempts never overlap.
    circuitBreaker: {
      failureThreshold: 10,
      failureWindowMs: 30000,
      halfOpenMaxProbes: 1,
      openMs: 30000,
    },
    hostConcurrency: 16,
    hosts: ['scanner-http-server-v2.core-prod', 'scanner-http-server-v2.core-staging'],
    name: 'scanner',
    queueTimeoutMs: 1000,
    timeoutMs: 2500,
  },
  {
    circuitBreaker: {
      failureThreshold: 5,
      failureWindowMs: 30000,
      halfOpenMaxProbes: 1,
      openMs: 60000,
    },
    hostConcurrency: 8,
    hosts: ['data-api-next.benzinga.com'],
    name: 'data-api-next',
    queueTimeoutMs: 2000,
    timeoutMs: 5000,
  },
  {
    circuitBreaker: {
      failureThreshold: 10,
      failureWindowMs: 20000,
      halfOpenMaxProbes: 2,
      openMs: 20000,
    },
    hostConcurrency: 18,
    hosts: ['money-layout.core-prod', 'money-layout.core-staging'],
    name: 'money-layout',
    queueTimeoutMs: 750,
    timeoutMs: 4000,
  },
  {
    circuitBreaker: {
      failureThreshold: 10,
      failureWindowMs: 30000,
      halfOpenMaxProbes: 2,
      openMs: 30000,
    },
    hostConcurrency: 18,
    hosts: ['editorial-tools.benzinga.com', 'editorial-tools.zingbot.bz'],
    name: 'editorial-tools',
    queueTimeoutMs: 2200,
    timeoutMs: 3000,
  },
  {
    circuitBreaker: {
      failureThreshold: 10,
      failureWindowMs: 30000,
      halfOpenMaxProbes: 2,
      openMs: 30000,
    },
    hostConcurrency: 16,
    hosts: ['content-server-next.pro-backend'],
    name: 'content-server-next',
    queueTimeoutMs: 1500,
    timeoutMs: 3000,
  },
  {
    circuitBreaker: {
      failureThreshold: 8,
      failureWindowMs: 30000,
      halfOpenMaxProbes: 1,
      openMs: 30000,
    },
    hostConcurrency: 12,
    hosts: ['www.benzinga.com'],
    name: 'www-benzinga-lavapress',
    pathPrefixes: ['/lavapress/api/'],
    queueTimeoutMs: 500,
    timeoutMs: 4000,
  },
  {
    circuitBreaker: {
      failureThreshold: 8,
      failureWindowMs: 30000,
      halfOpenMaxProbes: 2,
      openMs: 45000,
    },
    hostConcurrency: 24,
    hosts: ['www.benzinga.com'],
    name: 'www-benzinga-taxonomy',
    pathPrefixes: ['/services/taxonomy/'],
    queueTimeoutMs: 1500,
    timeoutMs: 2500,
  },
];

export const getUrlHost = (url: string): string => {
  try {
    return new URL(url).host || 'unknown';
  } catch {
    return 'unknown';
  }
};

const getUrlPathname = (url: string): string => {
  try {
    return new URL(url).pathname || '/';
  } catch {
    return '/';
  }
};

const policyMatchesUrl = (policy: CatalogOutboundRequestPolicy, host: string, pathname: string): boolean => {
  if (policy.developmentOnly && process.env.NODE_ENV !== 'development') return false;
  if (!policy.hosts.includes(host)) return false;
  if (!policy.pathPrefixes?.length) return true;
  return policy.pathPrefixes.some(prefix => pathname.startsWith(prefix));
};

export const getOutboundRequestPolicy = (url: string): OutboundRequestPolicy | undefined => {
  const host = getUrlHost(url);
  const pathname = getUrlPathname(url);
  return OUTBOUND_REQUEST_POLICIES.find(policy => policyMatchesUrl(policy, host, pathname));
};

export const getOutboundRequestConcurrencyKey = (url: string, policy?: OutboundRequestPolicy): string => {
  if (policy?.pathPrefixes?.length) {
    return `${getUrlHost(url)}:${policy.name}`;
  }
  return getUrlHost(url);
};
